// App.tsx
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './context/Auth/auth-context';
import { Layout } from './component/common/Layout';
import { ProtectedRoute } from './component/common/ProtectedRoute';
import { CreateQuiz } from './component/Admin/CreateQuiz';
import { QuizList } from './component/Admin/QuizList';
import { SessionManager } from './component/Admin/SessionManager';
import { PresenterView } from './component/Admin/PresenterView';
import { BotControl } from './component/Admin/BotControl';
import { BotHub } from './component/Admin/BotHub';
import { PresenterManager } from './component/Admin/PresenterManager';
import { HostProfile } from './component/Admin/HostProfile';
import { CrewManager } from './component/Admin/CrewManager';
import { PresenterPage } from './pages/PresenterPage';
import { JoinQuiz } from './component/Audience/JoinQuiz';
import { QuizView } from './component/Audience/QuizView';
import { Leaderboard } from './component/Audience/Leaderboard';
import { SignIn } from './pages/Auth/SignIn';
import { QuizSessions } from './component/Admin/QuizSessions';
import { DevPreview } from './pages/DevPreview';

function App() {
  const { user } = useAuth();

  return (
    <Layout>
      {/* Without this, every toast.success/error in the app silently no-ops —
          sign-in failures were invisible. */}
      <ToastContainer position="top-center" autoClose={2500} hideProgressBar theme="colored" />
      <Routes>
        {/* Public routes */}
        {/* TEMPORARY: unprotected UI preview for layout testing — remove with DevPreview.tsx */}
        <Route path="/dev/preview" element={<DevPreview />} />
        <Route
          path="/login"
          element={
            user ? <Navigate to="/" /> : <SignIn />
          }
        />
        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <JoinQuiz />
            </ProtectedRoute>
          }
        />
        {/* Host routes (admin + presenter) */}
        <Route
          path="/admin/create"
          element={
            <ProtectedRoute requireHost>
              <CreateQuiz />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/quizzes"
          element={
            <ProtectedRoute requireHost>
              <QuizList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/session/:sessionCode"
          element={
            <ProtectedRoute requireHost>
              <SessionManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/session/:sessionCode/present"
          element={
            <ProtectedRoute requireHost>
              <PresenterView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/profile"
          element={
            <ProtectedRoute requireHost>
              <HostProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/crews"
          element={
            <ProtectedRoute requireHost>
              <CrewManager />
            </ProtectedRoute>
          }
        />
        {/* Public presenter brand page — any signed-in user (players follow it
            from streams to find the next show). */}
        <Route
          path="/p/:uid"
          element={
            <ProtectedRoute>
              <PresenterPage />
            </ProtectedRoute>
          }
        />
        {/* Admin-only routes */}
        <Route
          path="/admin/presenters"
          element={
            <ProtectedRoute requireAdmin>
              <PresenterManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bots"
          element={
            <ProtectedRoute requireAdmin>
              <BotHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/session/:sessionCode/bots"
          element={
            <ProtectedRoute requireAdmin>
              <BotControl />
            </ProtectedRoute>
          }
        />
        {/* Audience routes */}
        <Route
          path="/quiz/:sessionCode"
          element={
            <ProtectedRoute>
              <QuizView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/quizzes/:quizId/sessions"
          element={
            <ProtectedRoute requireHost>
              <QuizSessions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz/:sessionCode/leaderboard"
          element={
            <ProtectedRoute>
              <Leaderboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/unauthorized"
          element={
            <div className="max-w-md mx-auto p-10 text-center">
              <h2 className="text-2xl font-bold font-display tracking-tight mb-2">Hosts only</h2>
              <p className="text-slate-500">This area is for quiz hosts. Ask an admin to grant you access.</p>
            </div>
          }
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Layout>
  );
}

export default App;
